import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trophy, Medal, Award, Pencil, Mail, Phone, MapPin, CreditCard, Camera, Loader2 } from "lucide-react";
import { membersApi, trophiesApi, financeApi, sportsApi, errMsg } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { PageContainer, EmptyState, UserAvatar, StatusBadge, SportChip, Money, SectionTitle } from "@/components/shared";
import { Reveal, StaggerGroup, StaggerItem, AnimatedCounter, Skeleton } from "@/components/motion";
import { ThemeSegmented } from "@/components/ThemeToggle";
import { hoverLift } from "@/lib/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const COUNTER_META = [
  { key: "athlete_week", label: "Athlète de la semaine", icon: Trophy, color: "text-trophy-gold" },
  { key: "coach_week", label: "Entraîneur de la semaine", icon: Medal, color: "text-trophy-gold" },
  { key: "athlete_month", label: "Athlète du mois", icon: Trophy, color: "text-trophy-silver" },
  { key: "coach_month", label: "Entraîneur du mois", icon: Medal, color: "text-trophy-silver" },
  { key: "athlete_year", label: "Athlète de l'année", icon: Award, color: "text-trophy-bronze" },
  { key: "coach_year", label: "Entraîneur de l'année", icon: Award, color: "text-trophy-bronze" },
];

function ProfileSkeleton() {
  return (
    <div>
      <div className="asl-hero-gradient asl-noise"><div className="mx-auto max-w-6xl px-4 sm:px-6 py-8"><div className="flex items-center gap-4"><Skeleton className="h-20 w-20 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-6 w-48" /><Skeleton className="h-4 w-32" /></div></div></div></div>
      <PageContainer>
        <Skeleton className="h-5 w-40" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}</div>
      </PageContainer>
    </div>
  );
}

export default function ProfilePage() {
  const { id } = useParams();
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const isSelf = !id || (user && id === user.id);
  const photoRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [showcase, setShowcase] = useState(null);
  const [payments, setPayments] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const targetId = id || user?.id;
      const [p, sc, sp] = await Promise.all([
        isSelf ? membersApi.me() : membersApi.card(targetId),
        trophiesApi.userShowcase(targetId),
        sportsApi.list(),
      ]);
      setProfile(p.data); setShowcase(sc.data); setSports(sp.data);
      setForm(p.data);
      if (isSelf) { try { const mp = await financeApi.myPayments(); setPayments(mp.data); } catch {} }
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id, user?.id]);

  const save = async () => {
    setBusy(true);
    try { const { data } = await membersApi.updateMe({ first_name: form.first_name, last_name: form.last_name, phone: form.phone, address: form.address, photo: form.photo, emergency_contact_name: form.emergency_contact_name, emergency_contact_phone: form.emergency_contact_phone }); setUser(data); setProfile(data); toast.success("Profil mis à jour"); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const onHeroPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image trop lourde (max 10 Mo)"); return; }
    setPhotoBusy(true);
    try {
      const photo = await compressImage(file);
      const { data } = await membersApi.updateMe({ photo });
      setUser(data); setProfile(data); setForm((f) => ({ ...f, photo }));
      toast.success("Photo de profil mise à jour");
    } catch (err) { toast.error(errMsg(err)); } finally { setPhotoBusy(false); if (photoRef.current) photoRef.current.value = ""; }
  };

  const onDialogPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image trop lourde (max 10 Mo)"); return; }
    try { const photo = await compressImage(file); setForm((f) => ({ ...f, photo })); } catch (err) { toast.error(errMsg(err)); }
  };

  const sportName = (sid) => sports.find((s) => s.id === sid)?.name;
  const sportColor = (sid) => sports.find((s) => s.id === sid)?.color;

  if (loading) return <ProfileSkeleton />;
  if (!profile) return <PageContainer><EmptyState title="Profil introuvable" /></PageContainer>;

  return (
    <div>
      <div className="relative overflow-hidden asl-hero-gradient asl-noise">
        <div className="pointer-events-none absolute inset-0">
          <div className="asl-blob absolute -left-8 top-4 h-40 w-40 rounded-full bg-secondary/20 blur-3xl" />
          <div className="asl-blob-2 absolute right-0 top-8 h-48 w-48 rounded-full bg-trophy-gold/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-8">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} className="flex flex-wrap items-center gap-4 text-white">
            <div className="relative">
              <UserAvatar user={profile} size="h-20 w-20" className="ring-4 ring-white/20" />
              {isSelf && (
                <label className="absolute -bottom-1 -right-1 cursor-pointer rounded-full bg-secondary p-1.5 text-white shadow-md transition-colors hover:bg-emerald-600" data-testid="profil-photo-bouton" title="Changer la photo">
                  {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onHeroPhoto} data-testid="profil-photo-input" />
                </label>
              )}
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-2xl font-bold">{profile.first_name} {profile.last_name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge status={profile.status} />
                {(profile.sports || []).map((sid) => sportName(sid) && <SportChip key={sid} name={sportName(sid)} color={sportColor(sid)} />)}
              </div>
            </div>
            {isSelf && (
              <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(profile); }}>
                <DialogTrigger asChild><Button variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/15" data-testid="profil-modifier-bouton"><Pencil className="mr-1.5 h-4 w-4" />Modifier</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Modifier mon profil</DialogTitle></DialogHeader>
                  <div className="flex items-center gap-4">
                    <UserAvatar user={form} size="h-16 w-16" />
                    <div className="space-y-1">
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-sm font-medium hover:bg-muted"><Camera className="h-4 w-4" />Téléverser une photo<input type="file" accept="image/*" className="hidden" onChange={onDialogPhoto} data-testid="profil-dialog-photo-input" /></label>
                      {form.photo && <button type="button" onClick={() => setForm((f) => ({ ...f, photo: null }))} className="block text-xs text-rose-600">Retirer la photo</button>}
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5"><Label>Prénom</Label><Input value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Nom</Label><Input value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Téléphone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Contact urgence</Label><Input value={form.emergency_contact_name || ""} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Tél. urgence</Label><Input value={form.emergency_contact_phone || ""} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button onClick={save} disabled={busy} data-testid="profil-enregistrer-bouton">{busy ? "…" : "Enregistrer"}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </motion.div>
        </div>
      </div>

      <PageContainer>
        <section className="space-y-3">
          <SectionTitle>Vitrine des trophées</SectionTitle>
          <StaggerGroup className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {COUNTER_META.map((m) => { const c = showcase?.counts?.[m.key] || 0; return (
              <StaggerItem key={m.key}>
                <motion.div {...(c ? hoverLift : {})}>
                  <Card className={c ? "border-secondary/30" : "opacity-60"}><CardContent className="flex flex-col items-center gap-1 p-4 text-center">
                    <m.icon className={`h-7 w-7 ${m.color}`} />
                    <AnimatedCounter value={c} className="text-2xl font-heading font-bold tabular-nums" />
                    <div className="text-[11px] leading-tight text-muted-foreground">{m.label}</div>
                  </CardContent></Card>
                </motion.div>
              </StaggerItem>
            ); })}
          </StaggerGroup>
          {showcase?.trophies?.length ? (
            <div className="mt-2 flex flex-wrap gap-2">{showcase.trophies.slice(0, 12).map((t) => (
              <Badge key={t.id} variant="outline" className="gap-1.5"><Trophy className="h-3 w-3 text-trophy-gold" />{t.type === "athlete" ? "Athlète" : "Entraîneur"} {t.period === "week" ? "semaine" : t.period === "month" ? "mois" : "année"} · {t.sport_name}</Badge>
            ))}</div>
          ) : <EmptyState title="Aucun trophée pour l'instant" icon={Trophy} />}
        </section>

        {isSelf && (
          <Reveal>
            <Card><CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
              <div>
                <h3 className="font-heading font-semibold">{t("theme.appearance")}</h3>
                <p className="text-sm text-muted-foreground">{t("theme.appearanceDesc")}</p>
              </div>
              <ThemeSegmented />
            </CardContent></Card>
          </Reveal>
        )}

        {isSelf && (
          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal><Card><CardContent className="space-y-2 p-5">
              <h3 className="font-heading font-semibold">Coordonnées</h3>
              <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-muted-foreground" />{profile.email}</div>
              <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-muted-foreground" />{profile.phone || "—"}</div>
              <div className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-muted-foreground" />{profile.address || "—"}</div>
              <div className="mt-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">Contact d'urgence : {profile.emergency_contact_name || "—"} · {profile.emergency_contact_phone || "—"}</div>
            </CardContent></Card></Reveal>

            <Reveal delay={0.08}><Card><CardContent className="p-5">
              <h3 className="mb-3 font-heading font-semibold">Historique des paiements</h3>
              {payments.length === 0 ? <EmptyState title="Aucun paiement" icon={CreditCard} /> : (
                <div className="space-y-2">{payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border p-2.5 text-sm">
                    <div><div className="font-medium">{p.purpose === "adhesion" ? "Adhésion" : "Cotisation"} · {p.method === "card" ? "Carte" : "Interac"}</div><div className="text-[11px] text-muted-foreground">{p.created_at?.slice(0, 10)}</div></div>
                    <div className="text-right"><Money value={p.amount} /><div className={`text-[11px] ${p.payment_status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>{p.payment_status === "paid" ? "Payé" : "En attente"}</div></div>
                  </div>
                ))}</div>
              )}
            </CardContent></Card></Reveal>
          </div>
        )}
      </PageContainer>
    </div>
  );
}
